import {
  PaginatedOptions,
  PaginatedResults,
} from "@sdk/utils/paginated-results";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Store } from "redux";
import { useEffectWhen } from "utils/hooks/use-effect-when";
import { useSimpleState } from "utils/hooks/use-simple-state";
import { useStateIfMountedX } from "utils/hooks/use-state-if-mounted";
import { iStore } from "./store.model";
import { useSelectorWithMemoize } from "./utils/use-selector-with-memoize";

export const useQueryWithStoreWithMemoize = <TSelected>(
  selectorFn: () => (state: iStore) => TSelected,
  selectorDeps: any[],
  useShallowEqualCheck: boolean,
  loaderFn: () => (store: Store, forceReload?: boolean) => Promise<any>,
  loaderDeps: any[],
  dependencies?: any[],
  disable?: boolean
) => {
  const store = useStore();
  const [retries, setRetries] = useStateIfMountedX(0);
  const [hasError, setHasError] = useStateIfMountedX(false);
  const [isLoading, setIsLoading] = useStateIfMountedX(false);
  const onRetry = useCallback(
    () => setRetries((retries) => retries + 1),
    [setRetries]
  );

  const value = useSelectorWithMemoize(
    selectorFn,
    selectorDeps,
    useShallowEqualCheck
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loader = useMemo(() => loaderFn(), loaderDeps);

  const loadData = useCallback(
    (force?: boolean) => {
      if (!disable) {
        setIsLoading(true);
        setHasError(false);
        const loaderPromise = loader(store, force);
        if (loaderPromise) {
          Promise.resolve(loaderPromise)
            .then((d) => {
              setIsLoading(false);
            })
            .catch((e) => {
              setHasError(true);
              setIsLoading(false);
            });
        }
      }
    },
    [disable, loader, setHasError, setIsLoading, store]
  );

  // Run on first run and when dependencies change
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(dependencies || []), disable]);

  // run only when retries change
  useEffectWhen(
    () => {
      loadData(true);
    },
    [loadData],
    [retries]
  );

  const returnValue = useMemo(
    () => ({
      state: value,
      isLoading,
      hasError,
      retry: onRetry,
    }),
    [hasError, isLoading, onRetry, value]
  );

  // useEffect(() => {
  //   console.log("value", value);
  // }, [value]);

  // useEffect(() => {
  //   console.log("isLoading", isLoading);
  // }, [isLoading]);

  // useEffect(() => {
  //   console.log("hasError", hasError);
  // }, [hasError]);

  // useEffect(() => {
  //   console.log("onRetry", onRetry);
  // }, [onRetry]);

  // useEffect(() => {
  //   console.log("returnValue", returnValue);
  // }, [returnValue]);

  return returnValue;
};

export const useQueryWithStore = <TSelected>(
  selector: (state: iStore) => TSelected,
  loader: (store: Store, forceReload?: boolean) => Promise<any>,
  dependencies?: any[],
  disable?: boolean
) => {
  const store = useStore();
  const [retries, setRetries] = useStateIfMountedX(0);
  const [hasError, setHasError] = useStateIfMountedX(false);
  const [isLoading, setIsLoading] = useStateIfMountedX(false);
  const onRetry = useCallback(
    () => setRetries((retries) => retries + 1),
    [setRetries]
  );
  const value = useSelector(selector);

  const loadData = useCallback(
    (force?: boolean) => {
      if (!disable) {
        setIsLoading(true);
        setHasError(false);
        const loaderPromise = loader(store, force);
        if (loaderPromise) {
          Promise.resolve(loaderPromise)
            .then((d) => {
              setIsLoading(false);
            })
            .catch((e) => {
              setHasError(true);
              setIsLoading(false);
            });
        }
      }
    },
    [disable, loader, setHasError, setIsLoading, store]
  );

  // Run on first run and when dependencies change
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(dependencies || []), disable]);

  // run only when retries change
  useEffectWhen(
    () => {
      loadData(true);
    },
    [loadData],
    [retries]
  );

  const returnValue = useMemo(
    () => ({
      state: value,
      isLoading,
      hasError,
      retry: onRetry,
    }),
    [hasError, isLoading, onRetry, value]
  );

  return returnValue;
};

export function useEntitySelector<Entity>(
  selector: (state: iStore) => any,
  entityId: string,
  loader: (store: Store, forceReload?: boolean) => any
) {
  const store = useStore();
  const [retries, setRetries] = useStateIfMountedX(0);
  const onRetry = useCallback(
    () => setRetries((retries) => retries + 1),
    [setRetries]
  );
  const value = useSelector(
    (state) => ((selector(state as any) as any).byIds as any)[entityId]
  );

  const isLoading = useSelector(
    (state) =>
      (
        ((selector(state as any) as any).itemsBeingFetched as any) || []
      ).indexOf(entityId) > -1
  );

  const hasError = useSelector(
    (state) =>
      (((selector(state as any) as any).failedItems as any) || []).indexOf(
        entityId
      ) > -1
  );
  useEffect(() => {
    loader(store, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retries, store]);

  const returnValue = useMemo(
    () => ({ value: value as Entity, isLoading, hasError }),
    [hasError, isLoading, value]
  );

  return returnValue;
}

export const usePaginatedQueryWithStore = <TSelected>(
  entityMapSelector: (state: iStore) => Record<string, TSelected>,
  entitiesSetter: any,
  query: any,
  queryCall: (req: {
    query: any;
    options: PaginatedOptions;
  }) => Promise<PaginatedResults<TSelected>>,
  dependencies?: any[],
  paginationOptions?: { pageSize?: number; currentPage?: number }
) => {
  const [paginationState, setPaginationState] = useSimpleState({
    lastQuery: "",
    // Query Config
    pageSize: 5,
    currentPage: 0,
    // Results
    totalItems: 0,
    // State
    isLoading: false,
    hasError: false,
    results: [] as string[],
    ...(paginationOptions || {}),
  });

  const entityMap = useSelector(entityMapSelector);
  const dispatch = useDispatch();
  const [didLoad, setDidLoad] = useState(false);

  useEffect(
    () => {
      setPaginationState({
        isLoading: true,
      });
      queryCall({
        query,
        options: {
          page:
            JSON.stringify(query) === paginationState.lastQuery
              ? paginationState.currentPage
              : 1,
          limit: paginationState.pageSize,
        },
      })
        .then((results) => {
          if (results.docs) {
            dispatch(entitiesSetter(results.docs));
          }

          setPaginationState({
            hasError: false,
            isLoading: false,
            results: results.docs.map((item) => (item as any).id),
            pageSize: results.limit,
            currentPage: results.page,
            totalItems: results.totalDocs,
            lastQuery: JSON.stringify(query),
          });
        })
        .catch((e) => {
          console.log("Error while querying", e);
          setPaginationState({
            hasError: true,
            isLoading: false,
          });
        });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ...(dependencies || []),
      paginationState.pageSize,
      paginationState.currentPage,
      query,
      didLoad,
    ]
  );

  if (!didLoad) {
    setDidLoad(true);
  }

  const returnValue = useMemo(
    () => ({
      paginationState,
      setPaginationState,
      results: paginationState.results.map((id) => entityMap[id]),
    }),
    [entityMap, paginationState, setPaginationState]
  );

  return returnValue;
};
